import React from 'react';
import { Avatar } from '@mui/material';
import Chip from 'Components/Chip';
import BasicTable from 'Components/BasicTable';
import { colors } from 'Constants/Colors';
import type { DashboardCourseI } from 'types';
import { ContainerTable, PeriodNote, TitleCell } from './CoursesTable.styled';

// Fila plana que consume BasicTable: las 5 primeras posiciones son las columnas
// visibles (limit=5), la 6a es la foto que pinta la celda del creador y la
// ultima es el curso tipado para resolver el click.
const TITLE_COLUMN = 0;
const CATEGORY_COLUMN = 3;
const CREATOR_COLUMN = 4;
const CREATOR_PHOTO_INDEX = 5;
const COURSE_INDEX = 6;

/**
 * Texto de la ventana vigente. Sin ciclo no hay ventana, y sin fecha límite
 * tampoco se pinta cuenta regresiva aunque el curso siga visible.
 */
const getCoursePeriodLabel = (course: DashboardCourseI): string | null => {
  if (!course.periodStartsAt) return null;
  if (!course.periodDueAt) return null;
  if (course.daysLeft === 0) return 'Último día para realizarlo';
  if (course.daysLeft === null) return `Vence el ${course.periodDueAt}`;
  return `Vence el ${course.periodDueAt} · ${course.daysLeft} días`;
};

// Tres días o menos: el estado se vuelve urgente.
const isUrgentPeriod = (course: DashboardCourseI): boolean => course.daysLeft !== null && course.daysLeft <= 3;

export interface CoursesTablePropsI {
  courses: DashboardCourseI[];
  colorCategory: Record<string, string>;
  headers: string[];
  loading: boolean;
  onCourseClick: (course: DashboardCourseI) => void;
}

export default function CoursesTable({ courses, colorCategory, headers, loading, onCourseClick }: CoursesTablePropsI) {
  const rows = courses.map(course => [
    course.title,
    course.description,
    course.priority?.name,
    course.category?.name,
    course.creator?.fullName,
    course.creator?.photo,
    course,
  ]);

  const thereIsComponent = (data: unknown[], index: number): React.ReactNode => {
    if (index === TITLE_COLUMN) {
      const course = data[COURSE_INDEX] as DashboardCourseI;
      const period = getCoursePeriodLabel(course);

      return (
        <TitleCell>
          <span>{data[index] as string}</span>
          {period && <PeriodNote urgent={isUrgentPeriod(course)}>{period}</PeriodNote>}
        </TitleCell>
      );
    }
    if (index === CATEGORY_COLUMN)
      return (
        <Chip style={{ border: 'none', background: `${colorCategory[data[index] as string]}`, color: colors.WHITE }}>
          {null}
          <h4>{data[index] as string}</h4>
        </Chip>
      );
    if (index === CREATOR_COLUMN)
      return (
        <div style={{ display: 'flex' }}>
          <Chip>
            <Avatar alt={data[index] as string} src={data[CREATOR_PHOTO_INDEX] as string} />
            <h4>{data[index] as string}</h4>
          </Chip>
        </div>
      );
    return data[index] as React.ReactNode;
  };

  return (
    <ContainerTable>
      <BasicTable
        information={rows}
        headers={headers}
        limit={5}
        thereIsComponent={thereIsComponent}
        widthColumn="25%"
        clickable
        clickAction={(row: unknown[]) => onCourseClick(row[COURSE_INDEX] as DashboardCourseI)}
        isLoading={loading}
      />
    </ContainerTable>
  );
}
